/**
 * Finance V2 — Assumptions dashboard + Investor Package Reports.
 */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { formatApiError } from "../../lib/errors";
import { Section, CategoryChip } from "./_shared";
import { Download, FileSpreadsheet, FileText, Edit3, Check, X } from "lucide-react";

export default function FinanceAssumptions() {
  const [rows, setRows] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [markingRow, setMarkingRow] = useState(null);
  const [sourceRef, setSourceRef] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/finance/v2/assumptions");
      setRows(data.assumptions || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (key) => {
    try {
      const numeric = parseFloat(editValue);
      const value = isNaN(numeric) ? editValue : numeric;
      await api.patch(`/finance/v2/assumptions/${key}`, { value });
      toast.success("Assumption updated");
      setEditingKey(null); setEditValue(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markAsSourced = async (row) => {
    // Marking as "actual" REQUIRES a non-empty source_reference (backend enforced).
    const ref = (sourceRef || "").trim();
    if (!ref) { toast.error("Source reference is required"); return; }
    try {
      await api.patch(`/finance/v2/assumptions/${row.key}`, {
        category: "actual",
        source_reference: ref,
      });
      toast.success("Marked as sourced (Actual)");
      setMarkingRow(null); setSourceRef(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revertToModeled = async (row) => {
    try {
      await api.patch(`/finance/v2/assumptions/${row.key}`, { category: "modeled" });
      toast.success("Reverted to Modeled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const download = async (kind) => {
    try {
      const suffix = kind === "xlsx" ? "xlsx" : "pdf";
      const res = await api.get(`/finance/v2/reports/investor-package.${suffix}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `investor_finance_package.${suffix}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const actualRows = rows.filter(r => r.category === "actual");
  const modeledRows = rows.filter(r => r.category !== "actual");

  return (
    <div className="p-6 max-w-6xl" data-testid="finance-assumptions-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0A2540]">Assumptions & Reports</h1>
          <div className="text-xs text-[#6B7280] mt-1">
            Actual/Historical values are validated. Modeled values are user-editable forward assumptions. Every Pro Forma output traces back here.
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => download("xlsx")}
                  className="h-9 px-4 rounded-md bg-white border border-[#E5E7EB] text-sm font-medium flex items-center gap-2 hover:bg-[#F9FAFB]"
                  data-testid="download-xlsx-btn">
            <FileSpreadsheet className="w-4 h-4" /> Investor Package · Excel
          </button>
          <button onClick={() => download("pdf")}
                  className="h-9 px-4 rounded-md bg-[#0A2540] text-white text-sm font-medium flex items-center gap-2 hover:bg-[#0F3564]"
                  data-testid="download-pdf-btn">
            <FileText className="w-4 h-4" /> Investor Package · PDF
          </button>
        </div>
      </div>

      <Section title="Actual / Historical / Contractual" category="contractual"
                subtitle="Values sourced from signed contracts, historical data, or validated production records. Each requires a source reference.">
        <AssumptionTable rows={actualRows} editingKey={editingKey}
                          editValue={editValue} setEditValue={setEditValue}
                          setEditingKey={setEditingKey} onSave={save}
                          markingRow={markingRow} setMarkingRow={setMarkingRow}
                          sourceRef={sourceRef} setSourceRef={setSourceRef}
                          onMarkAsSourced={markAsSourced} onRevertToModeled={revertToModeled} />
      </Section>

      <Section title="Modeled / Assumed (user-editable)" category="forecast"
                subtitle="Forward assumptions that drive the Pro Forma. Edit any value below. Click 'Mark as sourced' to promote to Actual (requires a source reference).">
        <AssumptionTable rows={modeledRows} editingKey={editingKey}
                          editValue={editValue} setEditValue={setEditValue}
                          setEditingKey={setEditingKey} onSave={save}
                          markingRow={markingRow} setMarkingRow={setMarkingRow}
                          sourceRef={sourceRef} setSourceRef={setSourceRef}
                          onMarkAsSourced={markAsSourced} onRevertToModeled={revertToModeled} />
      </Section>
    </div>
  );
}

const AssumptionTable = ({ rows, editingKey, editValue, setEditValue, setEditingKey, onSave,
                          markingRow, setMarkingRow, sourceRef, setSourceRef,
                          onMarkAsSourced, onRevertToModeled }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B7280] border-b border-[#E5E7EB]">
        <th className="py-2">Assumption</th><th>Value</th><th>Unit</th><th>Source reference</th><th>Notes</th><th></th>
      </tr>
    </thead>
    <tbody>
      {rows.length === 0 && (
        <tr><td colSpan={6} className="py-4 text-[#6B7280]">No assumptions in this category.</td></tr>
      )}
      {rows.map((r) => (
        <React.Fragment key={r.key}>
        <tr className="border-b border-[#F3F4F6]" data-testid={`assumption-${r.key}`}>
          <td className="py-2 pr-4">
            <div className="font-medium text-[#0A2540]">{r.display_name}</div>
            <div className="text-[10px] text-[#9CA3AF]">{r.key}</div>
          </td>
          <td className="tabular-nums">
            {editingKey === r.key ? (
              <input value={editValue} onChange={(e) => setEditValue(e.target.value)}
                      className="h-8 w-28 px-2 text-sm border border-[#E5E7EB] rounded-md tabular-nums"
                      data-testid={`edit-input-${r.key}`} />
            ) : String(r.value)}
          </td>
          <td className="text-xs text-[#6B7280]">{r.unit}</td>
          <td className="text-xs text-[#6B7280] max-w-[220px]">
            <div className="truncate" title={r.source_reference || r.source || "—"}>
              {r.source_reference || (r.category === "actual" ? "(no reference)" : r.source || "—")}
            </div>
          </td>
          <td className="text-xs text-[#6B7280] max-w-[200px]">{r.notes}</td>
          <td className="text-right whitespace-nowrap">
            {editingKey === r.key ? (
              <>
                <button onClick={() => onSave(r.key)} className="h-7 px-2 mr-1 text-xs bg-[#065F46] text-white rounded" data-testid={`save-${r.key}`}><Check className="w-3 h-3" /></button>
                <button onClick={() => setEditingKey(null)} className="h-7 px-2 text-xs bg-[#F3F4F6] rounded"><X className="w-3 h-3" /></button>
              </>
            ) : (
              <>
                <button onClick={() => { setEditingKey(r.key); setEditValue(String(r.value)); }}
                        className="h-7 px-2 mr-1 text-xs hover:bg-[#F3F4F6] rounded"
                        data-testid={`edit-btn-${r.key}`}><Edit3 className="w-3 h-3" /></button>
                {r.category === "actual" ? (
                  <button onClick={() => onRevertToModeled(r)}
                          className="h-7 px-2 text-[10px] uppercase tracking-wider bg-white border border-[#E5E7EB] rounded hover:bg-[#F9FAFB]"
                          data-testid={`revert-modeled-${r.key}`}>
                    Revert to Modeled
                  </button>
                ) : (
                  <button onClick={() => { setMarkingRow(r.key); setSourceRef(""); }}
                          className="h-7 px-2 text-[10px] uppercase tracking-wider bg-white border border-[#E5E7EB] rounded hover:bg-[#F9FAFB]"
                          data-testid={`mark-sourced-${r.key}`}>
                    Mark as sourced
                  </button>
                )}
              </>
            )}
          </td>
        </tr>
        {markingRow === r.key && (
          <tr className="bg-[#FFFBEB]" data-testid={`mark-sourced-row-${r.key}`}>
            <td colSpan={6} className="p-3">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">
                    Source reference (required) — e.g., "2025 audit sample, N=42" or "signed capital-provider term sheet #123"
                  </label>
                  <input value={sourceRef} onChange={(e) => setSourceRef(e.target.value)}
                          placeholder="Describe the authoritative source for this value…"
                          className="h-9 w-full px-2 text-sm border border-[#E5E7EB] rounded-md"
                          data-testid={`source-ref-input-${r.key}`} autoFocus />
                </div>
                <button onClick={() => onMarkAsSourced(r)}
                        disabled={!sourceRef.trim()}
                        className="h-9 px-3 text-xs font-medium bg-[#065F46] text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`confirm-mark-sourced-${r.key}`}>
                  Confirm
                </button>
                <button onClick={() => { setMarkingRow(null); setSourceRef(""); }}
                        className="h-9 px-3 text-xs bg-[#F3F4F6] rounded">Cancel</button>
              </div>
            </td>
          </tr>
        )}
        </React.Fragment>
      ))}
    </tbody>
  </table>
);
